// src/services/crossSellingService.ts
import { supabase } from '../utils/supabaseClient';
import {
  CrossSellingPayload,
  GetSuggestionsPayload,
  GetSuggestionsResponse,
  GetRelationshipsPayload,
  GetRelationshipsResponse,
  CrossSellingRelation,
  CrossSellingMenuItem,
} from '../types/crossSelling';

/**
 * Service for Cross-Selling API operations
 */
class CrossSellingService {
  /**
   * Get items suggestions (Manual or Auto) via RPC
   */
  async getItemsSuggestions(payload: GetSuggestionsPayload): Promise<GetSuggestionsResponse> {
    try {
      const { data, error } = await supabase
        .schema('cross_selling' as any)
        .rpc('get_items_suggestions' as any, {
          p_payload: payload as any,
        });

      if (error) throw error;

      return data as unknown as GetSuggestionsResponse;
    } catch (err) {
      console.error('Error fetching item suggestions:', err);
      return {
        success: false,
        message: 'Failed to fetch suggestions',
        data: [],
      };
    }
  }

  /**
   * Get manual cross-selling suggestions for a specific item (for modal)
   * Uses the get_cross_selling_relationships RPC
   */
  async getManualCrossSellingByItem(
    branchId: string,
    triggerItemId: string
  ): Promise<string[]> {
    try {
      const response = await this.getCrossSellingRelationships({
        branch_id: branchId,
        item_id: triggerItemId,
        is_manual: true,
        limit: 100,
        offset: 0
      });

      if (response.success && response.data) {
        // Flatten the nested structure to get suggested item IDs
        const suggestedIds: string[] = [];
        response.data.forEach(group => {
          if (group.relations && group.relations.length > 0) {
            group.relations.forEach(target => {
              suggestedIds.push(target.target_item_id);
            });
          }
        });
        return suggestedIds;
      }
      return [];
    } catch (err) {
      console.error('Error fetching cross-selling by item:', err);
      return [];
    }
  }

  /**
   * New RPC: Get grouped cross-selling relationships (for Management Page)
   */
  async getCrossSellingRelationships(payload: GetRelationshipsPayload): Promise<GetRelationshipsResponse> {
    try {
      const { data, error } = await supabase
        .schema('cross_selling' as any)
        .rpc('get_cross_selling_relationships' as any, {
          p_payload: payload as any,
        });

      if (error) throw error;

      return {
        success: data.success,
        message: data.message,
        data: data.data || [],
        pagination: data.pagination
      } as unknown as GetRelationshipsResponse;

    } catch (err) {
      console.error('Error fetching cross-selling relationships:', err);
      // Return empty structure on error
      return {
        success: false,
        message: 'Failed to fetch relationships',
        data: [],
        pagination: { limit: payload.limit || 10, offset: payload.offset || 0, total_items: 0 }
      };
    }
  }

  /**
   * Get cross-selling relations for a branch (for management page)
   * @param branchId - The branch ID
   * @param isManual - Filter: true=manual only, false=auto only, null/undefined=all
   */
  async getAllCrossSelling(branchId: string, isManual?: boolean | null): Promise<CrossSellingRelation[]> {
    try {
      // Build payload with optional is_manual filter
      const payload: GetRelationshipsPayload = {
        branch_id: branchId,
        limit: 100,
        offset: 0,
      };

      // Only include is_manual if explicitly set (not undefined)
      if (isManual !== undefined && isManual !== null) {
        payload.is_manual = isManual;
      }

      const response = await this.getCrossSellingRelationships(payload);

      if (!response.success || !response.data) {
        return [];
      }

      // Flatten the grouped response into CrossSellingRelation format
      const relations: CrossSellingRelation[] = [];

      response.data.forEach(group => {
        if (group.relations && group.relations.length > 0) {
          group.relations.forEach(target => {
            relations.push({
              id: `${group.source_item_id}-${target.target_item_id}`,
              trigger_item_id: group.source_item_id,
              trigger_item_name: group.source_item_name,
              trigger_item_image: group.source_image,
              suggested_item_id: target.target_item_id,
              suggested_item_name: target.target_item_name,
              is_manual: target.is_manual,
            });
          });
        }
      });

      return relations;
    } catch (err) {
      console.error('Error fetching cross-selling relations:', err);
      return [];
    }
  }


  /**
   * Save manual cross-selling rules for a trigger item
   * Note: RPC is PENDING. Keeping this temporarily or marking as TODO.
   */
  async setManualCrossSelling(payload: CrossSellingPayload): Promise<{ success: boolean }> {
    // TODO: Need 'update/save' RPC from backend
    console.warn('setManualCrossSelling: RPC not available yet', payload);
    return { success: false };
  }

  /**
   * Delete a manual cross-selling relation
   * Note: RPC is PENDING. Keeping this temporarily or marking as TODO.
   */
  async deleteManualCrossSelling(relationId: string): Promise<{ success: boolean }> {
    // TODO: Need 'delete' RPC from backend
    console.warn('deleteManualCrossSelling: RPC not available yet', relationId);
    return { success: false };
  }

  /**
   * Get all menu items for a branch (for selection in CrossSellingTab)
   * Uses existing items table
   */
  async getBranchMenuItems(branchId: string): Promise<CrossSellingMenuItem[]> {
    try {
      const { data, error } = await supabase
        .from('items')
        .select('id, name, image_url, price, ar')
        .eq('branch_id', branchId)
        .eq('is_published', true)
        .order('name');

      if (error) throw error;

      return (data || []) as CrossSellingMenuItem[];
    } catch (err) {
      console.error('Error fetching branch menu items:', err);
      return [];
    }
  }
}

export const crossSellingService = new CrossSellingService();
